/**
 * SafeZoneGuideService.ts
 * Service pour afficher les guides visuels de marges de sécurité (Bleed & Safe Zone)
 *
 * Affiche des lignes rouges pour indiquer :
 * - La zone de marge (Bleed) : zone qui sera coupée par l'imprimante
 * - La zone de sécurité (Safe Zone) : zone où placer le contenu important
 */

import Konva from 'konva';
import { useAppStore, getSafeZoneBoundsFromState, getBleedBoundsFromState } from './StateService';

/**
 * Service de guides de sécurité
 */
export class SafeZoneGuideService {
  private stage: Konva.Stage;
  private guideLayer: Konva.Layer | null = null;
  private bleedLines: Konva.Line[] = [];
  private safeZoneLines: Konva.Line[] = [];
  private isVisible: boolean = false;

  constructor(stage: Konva.Stage) {
    this.stage = stage;
    this.createGuideLayer();
    this.subscribeToProjectState();
  }

  /**
   * Crée le layer pour les guides
   */
  private createGuideLayer(): void {
    // Chercher ou créer le guideLayer
    this.guideLayer = this.stage.findOne('.guideLayer') as Konva.Layer | null;

    if (!this.guideLayer) {
      this.guideLayer = new Konva.Layer({
        name: 'guideLayer',
        listening: false, // Ne pas capturer les clics
      });
      this.stage.add(this.guideLayer);
    }
  }

  /**
   * S'abonne aux changements d'état du projet via Zustand store
   */
  private subscribeToProjectState(): void {
    // P3 : zustand v4 signature : un seul callback, diff manuel sur state.project.
    let lastProject = useAppStore.getState().project;
    useAppStore.subscribe(state => {
      if (state.project !== lastProject) {
        lastProject = state.project;
        this.updateGuides();
      }
    });
  }

  /**
   * Affiche ou masque les guides
   */
  public setVisible(visible: boolean): void {
    this.isVisible = visible;
    if (this.guideLayer) {
      this.guideLayer.visible(visible);
      if (visible) {
        this.updateGuides();
      }
      this.guideLayer.draw();
    }
  }

  /**
   * Met à jour les guides selon l'état actuel du projet
   */
  public updateGuides(): void {
    if (!this.guideLayer || !this.isVisible) return;

    const state = useAppStore.getState();
    if (!state.project) {
      this.clearGuides();
      return;
    }

    // Nettoyer les anciens guides
    this.clearGuides();

    // Récupérer les bounds depuis le store
    const bleedBounds = getBleedBoundsFromState(state);
    const safeZoneBounds = getSafeZoneBoundsFromState(state);

    if (!bleedBounds || !safeZoneBounds) return;

    // Créer les lignes de marge (Bleed) - Rouge foncé
    this.bleedLines = this.createBleedLines(bleedBounds, {
      width: state.project.pixelWidth,
      height: state.project.pixelHeight,
    });

    // Créer les lignes de zone de sécurité (Safe Zone) - Rouge clair
    this.safeZoneLines = this.createSafeZoneLines(safeZoneBounds, {
      width: state.project.pixelWidth,
      height: state.project.pixelHeight,
    });

    // Ajouter au layer
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.bleedLines.forEach(line => this.guideLayer!.add(line));
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.safeZoneLines.forEach(line => this.guideLayer!.add(line));

    this.guideLayer.draw();
  }

  /**
   * Crée les lignes de marge (Bleed)
   */
  private createBleedLines(
    bleedBounds: { x: number; y: number; width: number; height: number },
    canvasSize: { width: number; height: number }
  ): Konva.Line[] {
    const lines: Konva.Line[] = [];
    const strokeColor = '#ef4444'; // Rouge
    const strokeWidth = 2;
    const dash = [5, 5]; // Lignes pointillées

    // Ligne du haut (si bleedBounds.y < 0)
    if (bleedBounds.y < 0) {
      lines.push(
        new Konva.Line({
          points: [0, 0, canvasSize.width, 0],
          stroke: strokeColor,
          strokeWidth,
          dash,
          opacity: 0.7,
        })
      );
    }

    // Ligne du bas (si bleed dépasse)
    if (bleedBounds.y + bleedBounds.height > canvasSize.height) {
      lines.push(
        new Konva.Line({
          points: [0, canvasSize.height, canvasSize.width, canvasSize.height],
          stroke: strokeColor,
          strokeWidth,
          dash,
          opacity: 0.7,
        })
      );
    }

    // Ligne de gauche (si bleedBounds.x < 0)
    if (bleedBounds.x < 0) {
      lines.push(
        new Konva.Line({
          points: [0, 0, 0, canvasSize.height],
          stroke: strokeColor,
          strokeWidth,
          dash,
          opacity: 0.7,
        })
      );
    }

    // Ligne de droite (si bleed dépasse)
    if (bleedBounds.x + bleedBounds.width > canvasSize.width) {
      lines.push(
        new Konva.Line({
          points: [canvasSize.width, 0, canvasSize.width, canvasSize.height],
          stroke: strokeColor,
          strokeWidth,
          dash,
          opacity: 0.7,
        })
      );
    }

    return lines;
  }

  /**
   * Crée les lignes de zone de sécurité (Safe Zone)
   */
  private createSafeZoneLines(
    safeZoneBounds: { x: number; y: number; width: number; height: number },
    _canvasSize: { width: number; height: number }
  ): Konva.Line[] {
    const lines: Konva.Line[] = [];
    const strokeColor = '#f87171'; // Rouge clair
    const strokeWidth = 1.5;
    const dash = [3, 3]; // Lignes pointillées plus fines

    // Rectangle de zone de sécurité
    const x = safeZoneBounds.x;
    const y = safeZoneBounds.y;
    const width = safeZoneBounds.width;
    const height = safeZoneBounds.height;

    // Haut
    lines.push(
      new Konva.Line({
        points: [x, y, x + width, y],
        stroke: strokeColor,
        strokeWidth,
        dash,
        opacity: 0.6,
      })
    );

    // Bas
    lines.push(
      new Konva.Line({
        points: [x, y + height, x + width, y + height],
        stroke: strokeColor,
        strokeWidth,
        dash,
        opacity: 0.6,
      })
    );

    // Gauche
    lines.push(
      new Konva.Line({
        points: [x, y, x, y + height],
        stroke: strokeColor,
        strokeWidth,
        dash,
        opacity: 0.6,
      })
    );

    // Droite
    lines.push(
      new Konva.Line({
        points: [x + width, y, x + width, y + height],
        stroke: strokeColor,
        strokeWidth,
        dash,
        opacity: 0.6,
      })
    );

    return lines;
  }

  /**
   * Nettoie tous les guides
   */
  private clearGuides(): void {
    if (!this.guideLayer) return;

    this.bleedLines.forEach(line => line.destroy());
    this.safeZoneLines.forEach(line => line.destroy());
    this.bleedLines = [];
    this.safeZoneLines = [];
  }

  /**
   * Détruit le service
   */
  public destroy(): void {
    this.clearGuides();
    if (this.guideLayer) {
      this.guideLayer.destroy();
      this.guideLayer = null;
    }
  }
}
